import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import ContactCTA from "../components/ContactCTA";
import SolutionDescription from "../components/SolutionDescription";
import SolutionFeatures from "../components/SolutionFeatures";
import { Phone, Radio, Wifi, Users, Shield, ShieldCheck, Bug, Laptop } from "lucide-react";

const solutions = [
  {
    icon: Phone,
    key: "skydial",
    color: "text-textBlue",
    bgColor: "bg-blueLight",
    features: ["onpremise", "cloud", "hybrid", "branch", "sip"],
    hasCategories: true,
    isSkydial: true,
  },
  {
    icon: Radio,
    key: "voiceapi",
    color: "text-secondaryOrange",
    bgColor: "bg-orange-50",
    features: ["integration", "scalable", "reliable"],
  },
  {
    icon: Users,
    key: "aicounting",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    features: {
      benefits: ["traffic", "optimize", "marketing", "experience", "efficiency"],
      analytics: ["age", "gender", "employee", "reid", "accuracy"],
      behavioral: ["dwell", "flow", "anonymous", "realtime"],
    },
    hasCategories: true,
  },
  {
    icon: Shield,
    key: "vms",
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    features: {
      main: ["efficient", "professional", "compliance", "secure"],
      sectors: ["corporate", "government", "hospital", "education"],
    },
    hasCategories: true,
    hasSectors: true,
  },
  {
    icon: ShieldCheck,
    key: "soc",
    color: "text-red-600",
    bgColor: "bg-red-50",
    features: {
      main: ["visibility", "monitoring", "ai", "soccompliance", "sovereignty", "multitenancy"],
      architecture: ["collection", "normalization", "correlation", "storage", "alerting", "reporting"],
      logsources: ["network", "os", "identity", "soccloud", "endpoint", "applications"],
      usecases: ["abnormal", "privilege", "compromised", "ransomware", "insider", "massdownload", "multisite"],
      industries: ["manufacturing", "logistics", "healthcare", "government", "financial", "retail", "socprofessional"],
    },
    hasCategories: true,
    isSOC: true,
  },
  {
    icon: Bug,
    key: "vapt",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    features: {
      main: ["autonomous", "agentic", "onprem", "supervised", "boxmodes", "vaptreporting"],
      stages: ["upload", "assessment", "pentest", "report"],
      agents: ["orchestrator", "recon", "scanner", "exploit", "vaptreporter"],
      coverage: ["systemva", "mobile", "api", "server", "appvapt", "ad"],
      testingmodes: ["blackbox", "greybox", "whitebox"],
      impact: ["discovery", "turnaround", "toolchain", "reporttime"],
      trust: ["humanloop", "precision", "pilot"],
    },
    hasCategories: true,
    isVAPT: true,
  },
  {
    icon: Laptop,
    key: "firstprotect",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    features: {
      main: ["fpprevention", "fpondevice", "fpsigma", "fpcrossplatform", "fpuserspace", "fpmodules"],
      agentprotect: ["fpidentity", "fpenforcement", "fpforensics"],
      protectmodules: ["fpdata", "fpshell", "fpcredential", "fpapp", "fproot", "fpdestructive"],
      deployment: ["fpzerotouch", "fpdataplane", "fpauditmode", "fpoffline", "fpsoc", "fpalertquality"],
      proof: ["fpexfil", "fpoffline", "fpearlier"],
      stats: ["fplatency", "fpefficacy", "fpcpu", "fpplatforms"],
    },
    hasCategories: true,
    isFirstProtect: true,
  },
  {
    icon: Wifi,
    key: "networking",
    color: "text-textBlueDark",
    bgColor: "bg-backgroundTeal",
    features: ["bandwidth", "latency", "rural"],
  },
];

export default function SolutionDetail() {
  const { t } = useTranslation();
  const { solutionKey } = useParams();
  const navigate = useNavigate();

  const solution = solutions.find((s) => s.key === solutionKey);

  if (!solution) {
    return (
      <>
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Solution not found</h1>
            <button
              onClick={() => navigate("/solutions")}
              className="text-textBlue hover:text-textBlueDark font-semibold"
            >
              Back to Solutions
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = solution.icon;

  return (
    <>
      <div className="pt-20 bg-gradient-to-b from-white via-gray-50 to-white">
        {/* Header */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#e9f2fc] via-[#d0e7f9] to-[#c0dff7] py-16 sm:py-10 px-6 lg:px-16 md:px-8 sm:px-4">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="relative max-w-4xl mx-auto">
            <div className="flex flex-col items-start mb-6">
              <div className={`relative ${solution.bgColor} w-14 h-14 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg -mb-5 md:-mb-2 sm:mb-1`}>
                <div className="absolute inset-0 bg-white/20 rounded-xl transform rotate-6"></div>
                {solution.key === "skydial" ? (
                  <img
                    src="/logo skydial.svg"
                    alt="Skydial"
                    className="relative z-10 w-8 h-8 sm:w-7 sm:h-7 object-contain"
                  />
                ) : (
                  <Icon className={`relative z-10 w-7 h-7 sm:w-6 sm:h-6 ${solution.color}`} />
                )}
              </div>
              <h1 className="text-5xl lg:text-4xl md:text-3xl sm:text-2xl font-bold text-gray-900 leading-[1]">
                {t(`solutions.${solution.key}.title`)}
              </h1>
              {t(`solutions.${solution.key}.subtitle`) && (
                <p className="-mt-4 md:-mt-2 sm:mt-1 text-xl lg:text-lg md:text-base sm:text-sm text-gray-600 font-medium leading-tight">
                  {t(`solutions.${solution.key}.subtitle`)}
                </p>
              )}
            </div>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-textBlue to-transparent rounded-full"></div>
          </div>
        </section>

        {/* Description & Image Section */}
        <SolutionDescription solutionKey={solution.key} />

        {/* Features Section */}
        <SolutionFeatures solution={solution} />

        <ContactCTA />
        <Footer />
      </div>
    </>
  );
}
